import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InstallPWAButton from "@/components/InstallPWAButton";
import GAPageTracker from "@/components/GAPageTracker";
import { Toaster } from "@/components/ui/sonner";

// Lazy-load protected app pages for faster first paint on public routes
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const TransactionsPage = lazy(() => import("@/pages/TransactionsPage"));
const RecurringPage = lazy(() => import("@/pages/RecurringPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentCancelPage"));
const LedgerMembersPage = lazy(() => import("@/pages/LedgerMembersPage"));
const JoinLedgerPage = lazy(() => import("@/pages/JoinLedgerPage"));
const BlogIndexPage = lazy(() => import("@/pages/blog/BlogIndexPage"));
const BlogPostTrackMoney = lazy(() => import("@/pages/blog/BlogPostTrackMoney"));
const BlogPostCouplesBudget = lazy(() => import("@/pages/blog/BlogPostCouplesBudget"));
const BlogPostCategorizeExpenses = lazy(() => import("@/pages/blog/BlogPostCategorizeExpenses"));
const BlogPost503020vsCashflow = lazy(() => import("@/pages/blog/BlogPost503020vsCashflow"));
const BlogPostCouplesSavings = lazy(() => import("@/pages/blog/BlogPostCouplesSavings"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));

// Silence benign ResizeObserver loop warnings triggered by Recharts in dev mode
if (typeof window !== "undefined") {
  const origError = window.console.error;
  window.console.error = (...args) => {
    const msg = args?.[0];
    if (typeof msg === "string" && msg.includes("ResizeObserver loop")) return;
    origError.apply(window.console, args);
  };
  window.addEventListener("error", (e) => {
    if (e?.message && e.message.includes("ResizeObserver loop")) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  });
}

const AppLoading = (
  <div className="min-h-screen flex items-center justify-center bg-brand-bg">
    <div className="text-brand-muted font-body">Loading…</div>
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <GAPageTracker />
          <Suspense fallback={AppLoading}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <TransactionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recurring"
                element={
                  <ProtectedRoute>
                    <RecurringPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <BillingPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
              <Route
                path="/ledger/members"
                element={
                  <ProtectedRoute>
                    <LedgerMembersPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/join/:code" element={<JoinLedgerPage />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/how-to-track-money-coming-in-and-going-out" element={<BlogPostTrackMoney />} />
              <Route path="/blog/how-to-budget-as-a-couple" element={<BlogPostCouplesBudget />} />
              <Route path="/blog/how-to-categorize-your-expenses" element={<BlogPostCategorizeExpenses />} />
              <Route path="/blog/50-30-20-rule-vs-cash-flow-tracking" element={<BlogPost503020vsCashflow />} />
              <Route path="/blog/how-much-should-couples-save-each-month" element={<BlogPostCouplesSavings />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
          <InstallPWAButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
